'use client';

import React from 'react';
import { Play } from '@/lib/icons';

interface VSLPlaceholderProps {
  title?: string;
  duration?: string;
  className?: string;
}

/**
 * VSL Placeholder component that shows a preview image
 * Replace with actual video once rendered
 */
export const VSLPlaceholder: React.FC<VSLPlaceholderProps> = ({ 
  title = 'Stop Competing on Price. Start Winning on Infrastructure.',
  duration = '2:00',
  className = '' 
}) => {
  const [showInstructions, setShowInstructions] = React.useState(false);

  return (
    <div className={`my-12 ${className}`}>
      <div className="relative rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#294bfd] to-[#1e29cc]">
        <div className="pb-[56.25%] relative">
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Video Preview Content */}
            <div className="text-center text-white p-8 max-w-3xl mx-auto">
              {/* Play Button */}
              <button
                onClick={() => setShowInstructions(true)}
                className="mb-8 bg-white/10 backdrop-blur-sm rounded-full p-6 hover:bg-white/20 transition-all hover:scale-110 group"
              >
                <Play className="w-16 h-16 text-white fill-white/80 group-hover:fill-white" />
              </button>
              
              {/* Title */}
              <h2 className="text-4xl font-bold mb-4">
                {title}
              </h2>
              
              {/* Subtitle */}
              <p className="text-xl opacity-90 mb-6">
                Watch how 5,247+ loan officers are beating Rocket Mortgage at their own game
              </p>
              
              {/* Key Points */}
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  Real client results
                </span>
                <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  {duration} video
                </span>
                <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  Actionable insights
                </span>
              </div>
            </div>
          </div>
          
          {/* Instructions Modal */}
          {showInstructions && (
            <div className="absolute inset-0 bg-black/90 flex items-center justify-center p-8">
              <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Video Rendering Required
                </h3>
                <p className="text-gray-700 mb-6">
                  The VSL needs to be rendered to MP4 format for production display.
                </p>
                
                <div className="bg-gray-100 rounded-lg p-4 mb-6">
                  <p className="font-mono text-sm mb-2">
                    # Install FFmpeg (if not installed):
                  </p>
                  <p className="font-mono text-sm mb-4">
                    sudo apt update && sudo apt install ffmpeg -y
                  </p>
                  <p className="font-mono text-sm mb-2">
                    # Render the VSL:
                  </p>
                  <p className="font-mono text-sm">
                    ./render-vsl-production.sh
                  </p>
                </div>
                
                <p className="text-gray-600 text-sm mb-6">
                  Or view in Remotion Studio: 
                  <a href="http://localhost:3001" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1">
                    http://localhost:3001
                  </a>
                </p>
                
                <button
                  onClick={() => setShowInstructions(false)}
                  className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};